import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type OnboardingRow = {
  user_id: string;
  intent: "idea" | "funding" | "explore" | null;
  county: string | null;
  focus_area: string | null;
  organization: string | null;
  completed_at: string | null;
  tour_completed_at: string | null;
};

export type ChecklistItem = {
  id: string;
  label: string;
  done: boolean;
  href: string;
};

export function useOnboardingStatus() {
  const { user, loading: authLoading } = useAuth();

  const query = useQuery({
    enabled: !!user,
    queryKey: ["onboarding-status", user?.id],
    queryFn: async () => {
      const uid = user!.id;
      const [onboarding, profile, ideas, company, saved, follows] =
        await Promise.all([
          supabase
            .from("user_onboarding")
            .select("*")
            .eq("user_id", uid)
            .maybeSingle(),
          supabase
            .from("profiles")
            .select("display_name, avatar_url, location, stakeholder_type")
            .eq("user_id", uid)
            .maybeSingle(),
          supabase
            .from("ideas")
            .select("id", { count: "exact", head: true })
            .eq("user_id", uid),
          supabase
            .from("company_profiles")
            .select("id", { count: "exact", head: true })
            .eq("user_id", uid),
          supabase
            .from("saved_opportunities")
            .select("id", { count: "exact", head: true })
            .eq("user_id", uid),
          supabase
            .from("idea_followers")
            .select("idea_id", { count: "exact", head: true })
            .eq("user_id", uid),
        ]);

      const row = (onboarding.data ?? null) as OnboardingRow | null;
      const ideaCount = ideas.count ?? 0;
      const companyCount = company.count ?? 0;
      const savedCount = saved.count ?? 0;
      const followCount = follows.count ?? 0;
      const p = profile.data as
        | { display_name: string | null; avatar_url: string | null; location: string | null; stakeholder_type: string | null }
        | null;

      const essentialsComplete = !!(
        p?.display_name &&
        p.avatar_url &&
        p.location &&
        p.stakeholder_type
      );

      const checklist: ChecklistItem[] = [
        {
          id: "profile",
          label: "Set up your essentials",
          done: essentialsComplete,
          href: "/profile?tab=settings",
        },
        {
          id: "idea",
          label: "Post your first idea",
          done: ideaCount > 0,
          href: "/ideas",
        },
        {
          id: "coach",
          label: "Run your Coach assessment",
          done: companyCount > 0,
          href: "/coach",
        },
        {
          id: "save",
          label: "Save your first grant",
          done: savedCount > 0,
          href: "/coach",
        },
        {
          id: "follow",
          label: "Follow 3 ideas",
          done: followCount >= 3,
          href: "/ideas",
        },
      ];

      return {
        row,
        needsWelcome: !row?.completed_at,
        hasIdea: ideaCount > 0,
        hasCompanyProfile: companyCount > 0,
        tourCompleted: !!row?.tour_completed_at,
        checklist,
        progress: checklist.filter((c) => c.done).length / checklist.length,
      };
    },
  });

  return {
    loading: authLoading || query.isLoading,
    ...(query.data ?? {
      row: null as OnboardingRow | null,
      needsWelcome: false,
      hasIdea: false,
      hasCompanyProfile: false,
      tourCompleted: false,
      checklist: [] as ChecklistItem[],
      progress: 0,
    }),
    refetch: query.refetch,
  };
}